// @flow
import React, { useCallback } from "react";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import { isString } from "voca";
import getSymbolFromCurrency from "currency-symbol-map";
import t from "common/i18n";
import TabFooter from "root/components/TabFooter";
import Button from "root/components/Button";
import { selectIsCropEnabled } from "root/selectors";
import bindActions from "../../actions";
import {
	selectPurchaseDetails,
	selectCurrentSubscription,
	selectMediaType,
	selectPurchaseAssets,
	selectMetadataSchema,
	selectSelectedIds,
	selectIsPurchaseInProgress,
} from "../../selectors";
import type { Subscription, PurchaseDetails } from "../../data/types";
import SVG_ICONS from "~/assets/icons/icons";

type OwnProps = {|
    purchase: Function,
    isInPurchase: boolean,
    navigateToPurchase: Function,
    clearSelection: Function,
|};

type Props = {
    ...OwnProps,
    purchaseDetails: PurchaseDetails,
    currentSubscription: Subscription,
    isPurchaseInProgress: boolean,
    mediaType: string,
    assets: Object[],
    metadataSchema: Object[],
    selectedIds: string[],
    isCropEnabled: boolean,
    actions: { [string]: Function },
};

export const ACTION_TYPES = {
	ACQUIRE: "acquire",
	PURCHASE: "purchase",
	UPLOAD: "upload",
	NEXT: "next",
	DOWNLOADING: "downloading",
	PURCHASING: "purchasing",
};

const getPricePerDownload = (props: Props) => (props.currentSubscription
	? props.currentSubscription.price_per_download : null);

export const getPurchaseActionType = (props: Props, isLicensed: boolean) => {
	let text;

	if (props.isPurchaseInProgress) {
		text = isLicensed ? ACTION_TYPES.DOWNLOADING : ACTION_TYPES.PURCHASING;
	} else if (isLicensed) {
		text = props.isCropEnabled ? ACTION_TYPES.NEXT : ACTION_TYPES.UPLOAD;
	} else {
		text = getPricePerDownload(props)
			? ACTION_TYPES.PURCHASE
			: ACTION_TYPES.ACQUIRE;
	}

	return text;
};

const getPurchaseButtonText = (props: Props, price: string, actionType: string) => t(`shutterstock.purchase_page.button_label.${actionType}`);

const resolvePriceString = (props: Props, isLicenced) => {
	const downloadPrice = !isLicenced && getPricePerDownload(props);
	return downloadPrice
		? `${getSymbolFromCurrency(downloadPrice.local_currency)}${downloadPrice.local_amount}`
		: "";
};

export const getIsPurchaseValid = (props: Props, isLicenced: boolean) => {
	// TODO: move to reducer?
	let isValid = false;
	const { assets, purchaseDetails, metadataSchema } = props;

	if (assets.length && purchaseDetails.size) {
		isValid = true;

		const assetMetadata = purchaseDetails.metadata || {};

		if (metadataSchema && !isLicenced) {
			isValid = !metadataSchema.find((schemaItem: { key: string, required: boolean }) => (schemaItem.required
				? !assetMetadata[schemaItem.key] || !assetMetadata[schemaItem.key].length // if required, must have a value
				: !isString(assetMetadata[schemaItem.key]))); // Shutterstock expects empty strings for non required meta fields
		}
	}

	return isValid;
};

export const Footer = (props: Props) => {
	const { isInPurchase, purchase } = props;
	const licenceInfo = props.assets.length && props.assets[0].licenceInfo;
	const isLicenced = !!(licenceInfo && licenceInfo.is_downloadable);
	const isPurchaseValid = isInPurchase && getIsPurchaseValid(props, isLicenced);
	const downloadPrice = resolvePriceString(props, isLicenced);
	const actionType = getPurchaseActionType(props, isLicenced);

	const onClickPurchase = useCallback(() => purchase(isLicenced), [purchase, isLicenced]);

	const getActionButtonProps = () => (isInPurchase ? {
		actionButton: <Button
			className="action-button"
			colorName="link"
			dataTest="upload-btn"
			disabled={!isPurchaseValid}
			icon={SVG_ICONS[isLicenced ? "ARROW_FORWARD" : "CART"]}
			text={getPurchaseButtonText(props, downloadPrice, actionType)}
			onClick={onClickPurchase}
			inProgress={props.isPurchaseInProgress}
			key="footer_purchase_button"/>,
	}
		: {
			actionButtonText: t("shutterstock.next_btn"),
			actionButtonIcon: SVG_ICONS.ARROW_FORWARD,
			uploadButtonHandler: props.navigateToPurchase,
		});

	return <TabFooter showActionButton={!!props.selectedIds.length}
		actionButtonActive={!!props.selectedIds.length}
		{...getActionButtonProps()}
		clearSelectionHandler={props.clearSelection}>
		{isInPurchase && isPurchaseValid
        && <div className="w-50 mr6 b">
        	{downloadPrice}
        </div>}
	</TabFooter>;
};

export default connect<Props, OwnProps, _, _, _, _>(
	createStructuredSelector({
		purchaseDetails: selectPurchaseDetails,
		currentSubscription: selectCurrentSubscription,
		isPurchaseInProgress: selectIsPurchaseInProgress,
		isCropEnabled: selectIsCropEnabled,
		mediaType: selectMediaType,
		assets: selectPurchaseAssets,
		metadataSchema: selectMetadataSchema,
		selectedIds: selectSelectedIds,
	}),
	(dispatch: Function) => ({
		actions: bindActions(dispatch),
	}),
)(Footer);
